import { DataTable, EmptyState, type DataTableColumn } from '@wundo/ui';
import type { OnboardingNudgeKey } from '@wundo/shared/onboardingNudge.ts';
import type { NudgeKeyCounts } from '@/lib/dashboard.ts';
import { ChartCard } from './ChartCard.tsx';
import { METRIC_INFO } from './metricsInfo.ts';

/** The nudges as the app words them, not as the database keys them. */
const NUDGE_LABELS: Record<OnboardingNudgeKey, string> = {
  plan_first_meeting: 'Plan your first conversation',
  try_library_roleplay: 'Try a practice session from the library',
  rehearse_this_meeting: 'Rehearse this conversation',
  ask_conversation_tips: 'Ask for conversation tips',
  enable_notetaker: 'Turn on the notetaker',
  ask_meeting_feedback: 'Ask for feedback on the conversation',
  upload_person_document: 'Upload a document for this person',
};

function completionRate(row: NudgeKeyCounts): string {
  return row.shown === 0 ? '–' : `${Math.round((row.completed / row.shown) * 100)}%`;
}

const COLUMNS: DataTableColumn<NudgeKeyCounts>[] = [
  { key: 'nudge_key', header: 'Nudge', render: (row) => NUDGE_LABELS[row.nudge_key] },
  { key: 'shown', header: 'Shown', align: 'end', width: 'w-20', nowrap: true },
  { key: 'dismissed', header: 'Dismissed', align: 'end', width: 'w-24', nowrap: true },
  { key: 'completed', header: 'Completed', align: 'end', width: 'w-24', nowrap: true },
  {
    key: 'rate',
    header: 'Rate',
    align: 'end',
    width: 'w-20',
    nowrap: true,
    render: completionRate,
  },
];

export interface NudgeBreakdownTableProps {
  rows: NudgeKeyCounts[];
}

/** Lifetime totals per nudge — the one block on this page the date filter misses. */
export function NudgeBreakdownTable({ rows }: NudgeBreakdownTableProps) {
  const anyData = rows.some(
    (row) => row.shown > 0 || row.dismissed > 0 || row.completed > 0,
  );

  return (
    <ChartCard
      title='Per nudge'
      info={METRIC_INFO.nudgePerKey}
      description='Lifetime totals per nudge. The date filter does not apply; clicks are not recorded per nudge.'
    >
      <DataTable<NudgeKeyCounts>
        label='Per nudge'
        columns={COLUMNS}
        rows={anyData ? rows : []}
        rowKey={(row) => row.nudge_key}
        emptyState={<EmptyState title='No data yet' />}
      />
    </ChartCard>
  );
}
